'use client';

import { type Ref, useCallback, useEffect, useRef } from 'react';

import { useMergeRefs } from '@floating-ui/react';
import classnames from 'classnames';
import { IoSearch } from 'react-icons/io5';

import { IconButton } from '../../icon-button';
import { Input } from '../input/Input';
import type { SearchProps } from './Search.types';

import styles from './Search.module.scss';

export function Search({
  style,
  className,
  error,
  onSearch,
  debounceTime = 300,
  onChange,
  testId = 'ui-search',
  ref,
  ...rest
}: SearchProps & { ref?: Ref<HTMLInputElement> }) {
  const nodeRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mergeRef = useMergeRefs([ref, nodeRef]);

  const validatedDebounceTime = Math.max(0, debounceTime);

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearDebounceTimer();
    return () => {
      clearDebounceTimer();
    };
  }, [debounceTime, onChange, clearDebounceTimer]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        clearDebounceTimer();

        debounceTimerRef.current = setTimeout(() => {
          onChange(e);
          debounceTimerRef.current = null;
        }, validatedDebounceTime);
      }
    },
    [onChange, validatedDebounceTime, clearDebounceTimer],
  );

  const handleSearch = useCallback(() => {
    clearDebounceTimer();

    if (onSearch) {
      onSearch(nodeRef.current?.value ?? '');
    }
  }, [onSearch, clearDebounceTimer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onSearch && e.key === 'Enter') {
        handleSearch();
      }
    },
    [onSearch, handleSearch],
  );

  return (
    <div
      style={style}
      className={classnames(styles['search'], className)}
      data-testid={testId}
    >
      <Input
        ref={mergeRef}
        className={styles['input']}
        error={error}
        onChange={handleChange}
        onKeyDown={onSearch ? handleKeyDown : undefined}
        data-testid={testId}
        {...rest}
      />
      <IconButton
        size="sm"
        icon={<IoSearch />}
        disabled={rest.disabled}
        aria-label="Search"
        className={classnames(styles['icon'], error && styles[`icon--error`])}
        onClick={handleSearch}
      />
    </div>
  );
}
